import { Button } from 'components/atoms/Button';
import ExclaimationTriangleIcon from 'components/atoms/SVG/ExclaimationTrinagleIcon';
import SendMoneyIcon from 'components/atoms/SVG/SendMoneyIcon.component';
import ModalHeading from 'components/molecules/ModalHeading';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { fadeTransition } from 'utils/constants';
import type { TransferStepProps } from '../types';
import AccountSelector from './AccountSelector';
import type { Connector } from 'types/transactions.types';

interface AccountSelectionStepProps extends TransferStepProps {
  sourceAccount?: Connector;
  receiptAccount?: Connector | 'external';
  sourceAccountOptions: any[];
  receiptAccountOptions: any[];
  hideReceiptAccountSelector?: boolean;
  onSourceAccountChange: (account: Connector) => void;
  onReceiptAccountChange: (account: Connector | 'external') => void;
  canProceed: boolean;
  insufficientBalance?: boolean;
}
const AccountSelectionStep: FC<AccountSelectionStepProps> = ({
  sourceAccount,
  receiptAccount,
  sourceAccountOptions,
  receiptAccountOptions,
  hideReceiptAccountSelector,
  onSourceAccountChange,
  onReceiptAccountChange,
  onNext,
  canProceed,
  insufficientBalance,
}) => {
  const { t } = useTranslation();

  return (
    <motion.div
      className="flex items-center flex-col relative h-full px-6 w-full"
      {...fadeTransition}
      key="selection"
    >
      <ModalHeading
        Icon={SendMoneyIcon}
        title={t('accountable_banking.send_money.label')}
        subtitle={t('accountable_banking.send_money.subtitle')}
      />

      <motion.div {...fadeTransition} className="w-full my-6">
        {/* Source Account Selection */}
        <AccountSelector
          name="sourceAccount"
          label={t('accountable_banking.review_transfer.from')}
          placeholder={t(
            'accountable_banking.send_money.choose_source_account',
          )}
          options={sourceAccountOptions}
          value={sourceAccount?._id || null}
          onChange={onSourceAccountChange}
        />

        {!hideReceiptAccountSelector && (
          <AccountSelector
            name="receiptAccount"
            label={t('accountable_banking.review_transfer.to')}
            placeholder={t(
              'accountable_banking.send_money.choose_recipient_account',
            )}
            options={receiptAccountOptions}
            value={
              receiptAccount === 'external'
                ? 'external'
                : receiptAccount?._id || null
            }
            onChange={onReceiptAccountChange}
          />
        )}

        {/* Insufficient Balance Warning */}
        {insufficientBalance && (
          <div className="flex items-center space-x-3 mb-6">
            <ExclaimationTriangleIcon className="w-5 stroke-red-500" />
            <span className="text-red-500">
              {t('accountable_banking.send_money.insufficient_balance')}
            </span>
          </div>
        )}

        {/* Continue Button */}
        <Button className="w-full" disabled={!canProceed} onClick={onNext}>
          {t('continue')}
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default AccountSelectionStep;
